// Pickup RPCs — thin wrappers around the trusted Supabase functions.
// Supabase is authoritative for pickup validity; the frontend uses the
// returned rules only to guide the UI. Never duplicates the 24-hour
// rule or store-hours logic as authority.
//
// Timezone rule:
//   The customer picks wall-clock time IN THE STORE's timezone (returned
//   by get_pickup_rules). buildStoreZonedIso() takes YYYY-MM-DD + HH:mm
//   and produces the ISO-8601 UTC instant that corresponds to that
//   wall-clock in the store timezone, so Supabase's timestamptz parse
//   preserves the customer's intended local time.

import { supabase, supabaseConfigured } from './supabase';

function logSanitized(label, err) {
  try {
    console.warn('[etff:pickup]', label, {
      name: err?.name || null,
      code: err?.code || null,
      message: err?.message || null,
    });
  } catch { /* ignore */ }
}

// ---------- RPCs ----------

export async function rpcGetPickupRules() {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'Pickup is temporarily unavailable.' };
  }
  try {
    const { data, error } = await supabase.rpc('get_pickup_rules');
    if (error) {
      logSanitized('get_pickup_rules:error', error);
      return { ok: false, data: null, error: 'We could not load pickup times. Please try again.' };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized('get_pickup_rules:threw', err);
    return { ok: false, data: null, error: 'We could not load pickup times. Please try again.' };
  }
}

export async function rpcSelectPickupTime(pickupAtIso) {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'Pickup is temporarily unavailable.' };
  }
  if (typeof pickupAtIso !== 'string' || pickupAtIso.length === 0) {
    return { ok: false, data: null, error: 'Please choose a valid pickup time.' };
  }
  try {
    const { data, error } = await supabase.rpc('select_pickup_time', {
      p_pickup_at: pickupAtIso,
    });
    if (error) {
      logSanitized('select_pickup_time:error', error);
      return { ok: false, data: null, error: "That pickup time isn't available. Please choose another time." };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized('select_pickup_time:threw', err);
    return { ok: false, data: null, error: "That pickup time isn't available. Please choose another time." };
  }
}

// ---------- Tolerant readers over the rules payload ----------

export function readPickupOutcome(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const o = payload.outcome;
  return typeof o === 'string' ? o : null;
}

export function readConfirmedPickupAt(payload) {
  if (!payload || typeof payload !== 'object') return null;
  for (const k of ['pickup_at', 'confirmed_pickup_at', 'selected_pickup_at', 'requested_pickup_at']) {
    const v = payload[k];
    if (typeof v === 'string' && v.length) return v;
  }
  // Some RPCs wrap the payload in { pickup: {...} }.
  const inner = payload.pickup;
  if (inner && typeof inner === 'object') return readConfirmedPickupAt(inner);
  return null;
}

export function readPickupMinLeadHours(rules) {
  if (!rules || typeof rules !== 'object') return null;
  for (const k of ['min_lead_hours', 'min_lead_time_hours', 'lead_time_hours', 'minimum_lead_hours']) {
    const v = rules[k];
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

export function readStoreTimezone(rules) {
  if (!rules || typeof rules !== 'object') return null;
  for (const k of ['store_timezone', 'timezone', 'store_tz', 'tz']) {
    const v = rules[k];
    if (typeof v === 'string' && v.length) return v;
  }
  return null;
}

// Given the rules payload and a YYYY-MM-DD date string, resolve the
// store hours applicable to that date in the store's timezone.
// Accepts a variety of shapes:
//   rules.hours       -> [{ day_of_week:0..6, open:"HH:mm", close:"HH:mm", closed?:bool }]
//   rules.store_hours -> same
//   rules.weekly_hours-> same
//   rules.business_hours -> same
// Returns { open, close, closed } or null when it cannot be determined.
export function readStoreHoursForDate(rules, dateStr) {
  if (!rules || typeof rules !== 'object' || !dateStr) return null;
  let arr = null;
  for (const k of ['hours', 'store_hours', 'weekly_hours', 'business_hours']) {
    if (Array.isArray(rules[k])) { arr = rules[k]; break; }
  }
  if (!arr) return null;
  const tz = readStoreTimezone(rules);
  const dow = dayOfWeekInTz(dateStr, tz);
  if (dow === null) return null;
  const match = arr.find((h) => {
    const d = Number(h?.day_of_week ?? h?.dow ?? h?.day);
    return Number.isFinite(d) && d === dow;
  });
  if (!match) return null;
  const open = pickTimeStr(match, ['open', 'open_time', 'opens_at', 'from']);
  const close = pickTimeStr(match, ['close', 'close_time', 'closes_at', 'to']);
  const closed = match.closed === true || (!open && !close);
  return { open, close, closed };
}

function pickTimeStr(o, keys) {
  for (const k of keys) {
    const v = o?.[k];
    if (typeof v !== 'string') continue;
    // Accept "HH:mm" or "HH:mm:ss".
    const m = v.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (m) return `${m[1].padStart(2, '0')}:${m[2]}`;
  }
  return null;
}

// Day-of-week (0..6, Sunday=0) for a YYYY-MM-DD in the given tz (or
// browser tz if none supplied).
function dayOfWeekInTz(dateStr, tz) {
  const [y, m, d] = dateStr.split('-').map(Number);
  if (![y, m, d].every(Number.isFinite)) return null;
  // Anchor the date at 12:00 to avoid DST edge flips.
  const anchorUtc = Date.UTC(y, m - 1, d, 12, 0, 0);
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz || undefined,
    weekday: 'short',
  });
  const wk = dtf.format(new Date(anchorUtc));
  const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return map[wk] ?? null;
}

// ---------- Timezone-correct ISO construction ----------

// Compute the offset (ms) that must be subtracted from a UTC instant
// so that its wall-clock representation in `tz` equals the same
// year/month/day/hour/minute/second the caller intended.
function tzOffsetMsForWallClock(y, m, d, hh, mm, ss, tz) {
  // Take the naïve UTC instant that has those wall-clock digits.
  const guess = Date.UTC(y, m - 1, d, hh, mm, ss);
  // Ask the platform what wall-clock those digits look like in `tz`.
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(new Date(guess));
  const P = {};
  for (const p of parts) if (p.type !== 'literal') P[p.type] = p.value;
  const hour = Number(P.hour) === 24 ? 0 : Number(P.hour);
  const asUtcInTz = Date.UTC(
    Number(P.year), Number(P.month) - 1, Number(P.day),
    hour, Number(P.minute), Number(P.second)
  );
  return asUtcInTz - guess;
}

// Build an ISO-8601 UTC timestamp for wall-clock (dateStr, timeStr) in
// the given store timezone. Falls back to the browser's local timezone
// when tz is missing.
export function buildStoreZonedIso(dateStr, timeStr, tz) {
  if (!dateStr || !timeStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const [hh, mm] = timeStr.split(':').map(Number);
  if (![y, m, d, hh, mm].every(Number.isFinite)) return null;
  if (!tz) {
    const dt = new Date(y, m - 1, d, hh, mm, 0, 0);
    if (Number.isNaN(dt.getTime())) return null;
    return dt.toISOString();
  }
  try {
    const offset = tzOffsetMsForWallClock(y, m, d, hh, mm, 0, tz);
    const utcMs = Date.UTC(y, m - 1, d, hh, mm, 0) - offset;
    return new Date(utcMs).toISOString();
  } catch {
    // If Intl fails (unknown tz), fall back to browser local.
    const dt = new Date(y, m - 1, d, hh, mm, 0, 0);
    return Number.isNaN(dt.getTime()) ? null : dt.toISOString();
  }
}

// Format an ISO instant for customer-facing display in the store tz.
export function fmtHumanInTz(iso, tz) {
  try {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return iso;
    return dt.toLocaleString(undefined, {
      timeZone: tz || undefined,
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

// Extract a persisted requested_pickup_at from the raw
// get_selected_meal_plan payload (or nested plan object). Used to
// rehydrate confirmed pickup after a refresh WITHOUT relying on
// sessionStorage as authority.
export function readPersistedPickupFromPlanPayload(rawPayload) {
  if (!rawPayload || typeof rawPayload !== 'object') return null;
  const plan = rawPayload.plan && typeof rawPayload.plan === 'object' ? rawPayload.plan : rawPayload;
  for (const k of ['requested_pickup_at', 'pickup_at', 'confirmed_pickup_at', 'selected_pickup_at']) {
    const v = plan?.[k] || rawPayload?.[k];
    if (typeof v === 'string' && v.length) return v;
  }
  return null;
}

// Earliest date allowed by the min-lead-time (server still validates).
// Returned as YYYY-MM-DD in the store's timezone so the HTML5 date
// input's `min` attribute lines up with what the customer sees.
export function earliestPickupDateInTz(minLeadHours, tz) {
  const hours = Number.isFinite(minLeadHours) ? Math.max(0, minLeadHours) : 24;
  const nowUtc = Date.now() + hours * 3600 * 1000;
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz || undefined,
    year: 'numeric', month: '2-digit', day: '2-digit',
  });
  // en-CA gives YYYY-MM-DD natively.
  const parts = dtf.formatToParts(new Date(nowUtc));
  const P = {};
  for (const p of parts) if (p.type !== 'literal') P[p.type] = p.value;
  return `${P.year}-${P.month}-${P.day}`;
}
